/**
 * Learn — the cause-first path.
 *
 * A lesson is not finished when you have read it. Each unit ends in three tasks:
 * write it, hear it, use it — and the mastery rule for that unit decides which of
 * them must be satisfied. Progress is stored locally, so the path survives closing
 * the app and works offline.
 *
 * This is an original learning path inspired by publicly described cause-first
 * teaching. It is not a reproduction of any book and implies no endorsement.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WritingPad, type WritingPadHandle } from '../components/WritingPad';
import { Score } from '../components/Score';
import { recordPractice } from '../curriculum/practice';
import { LESSONS } from '../curriculum/lessons';
import {
  createBackend,
  dueForReview,
  isUnlocked,
  nextLesson,
  recordCreative,
  recordEar,
  recordInk,
  scheduleIfMastered,
  type ProgressBackend,
} from '../curriculum/progress';
import { emptyProgress, isMastered, type Lesson, type LessonProgress } from '../curriculum/types';
import { buildEarExample, type EarExample } from '../curriculum/earTraining';
import type { RecognitionResult } from '../ink/recognize';
import { parsePitch, pitchName, type Pitch } from '../theory/pitch';
import { playPitches, stop } from '../audio/player';

type TaskTab = 'write' | 'hear' | 'use';

const parseAll = (list: string[]): Pitch[] =>
  list.map((t) => {
    const p = parsePitch(t);
    if (!p) throw new Error(`bad pitch in curriculum: ${t}`);
    return p;
  });

function matches(written: Pitch[], expected: Pitch[], ignoreOctave: boolean): boolean {
  if (written.length !== expected.length) return false;
  const remaining = [...written];
  for (const want of expected) {
    const index = remaining.findIndex(
      (p) =>
        p.letter === want.letter &&
        p.alter === want.alter &&
        (ignoreOctave || p.octave === want.octave),
    );
    if (index < 0) return false;
    remaining.splice(index, 1);
  }
  return true;
}
import { Button } from '../design/Button';
import { TabPanel, Tabs } from '../design/Navigation';

export function Learn() {
  const inkPadRef = useRef<WritingPadHandle>(null);
  const backendRef = useRef<ProgressBackend | null>(null);

  const [progress, setProgress] = useState<Record<string, LessonProgress>>({});
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<string>(LESSONS[0].id);
  const [tab, setTab] = useState<TaskTab>('write');

  const [inkResult, setInkResult] = useState<RecognitionResult | null>(null);
  const [inkVerdict, setInkVerdict] = useState<string | null>(null);
  const [example, setExample] = useState<EarExample | null>(null);
  const [earVerdict, setEarVerdict] = useState<string | null>(null);
  const [creativeShown, setCreativeShown] = useState(false);
  /**
   * When the current exercise began.
   *
   * Reset whenever the lesson or the ear example changes, so a recorded session
   * spans the attempt rather than the whole time the tab happened to be open.
   */
  const startedAtRef = useRef(Date.now());
  /** Answers right in a row in the run now in progress. */
  const earRunRef = useRef(0);

  // --- persistence ------------------------------------------------------
  useEffect(() => {
    const backend = createBackend();
    backendRef.current = backend;
    let cancelled = false;
    backend
      .load()
      .then((stored) => {
        if (cancelled) return;
        const merged: Record<string, LessonProgress> = {};
        for (const lesson of LESSONS) merged[lesson.id] = stored[lesson.id] ?? emptyProgress(lesson.id);
        setProgress(merged);
        setSelectedId(nextLesson(merged)?.id ?? LESSONS[0].id);
        setLoaded(true);
      })
      .catch(() => {
        // The same guard the success path has. Without it a screen torn down
        // while storage was failing still tried to fill itself in.
        if (cancelled) return;
        const fresh: Record<string, LessonProgress> = {};
        for (const lesson of LESSONS) fresh[lesson.id] = emptyProgress(lesson.id);
        setProgress(fresh);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = useCallback((lesson: Lesson, change: (p: LessonProgress) => LessonProgress) => {
    setProgress((current) => {
      const before = current[lesson.id] ?? emptyProgress(lesson.id);
      const after = scheduleIfMastered(lesson, change(before), Date.now());
      void backendRef.current?.save(after);
      return { ...current, [lesson.id]: after };
    });
  }, []);

  const lesson = useMemo(
    () => LESSONS.find((l) => l.id === selectedId) ?? LESSONS[0],
    [selectedId],
  );
  const current = progress[lesson.id] ?? emptyProgress(lesson.id);
  const mastered = isMastered(lesson, current);
  const unlocked = isUnlocked(lesson, progress);
  const due = useMemo(() => dueForReview(progress, Date.now()), [progress]);

  // A new lesson means a new ear example and a clean pad.
  useEffect(() => {
    setExample(buildEarExample(lesson.earTask));
    setEarVerdict(null);
    setInkVerdict(null);
    setCreativeShown(false);
    setTab('write');
    startedAtRef.current = Date.now();
    earRunRef.current = 0;
  }, [lesson]);

  const demoGroups = useMemo(
    () => lesson.demonstration.groups.map(parseAll),
    [lesson],
  );
  const expected = useMemo(() => parseAll(lesson.guidedInkTask.expect), [lesson]);

  const checkInk = () => {
    const result = inkPadRef.current?.recognizeNow() ?? inkResult;
    const written = result?.notes.map((n) => n.pitch) ?? [];
    const passed = matches(written, expected, lesson.guidedInkTask.ignoreOctave);
    setInkVerdict(
      passed
        ? 'Correct.'
        : written.length === 0
          ? 'Nothing read from the staff yet.'
          : `Read as ${written.map(pitchName).join(', ')} — expected ${expected.map(pitchName).join(', ')}.`,
    );
    update(lesson, (p) => recordInk(p, passed, Date.now()));
    // A checked answer is a completed exercise. The handwriting itself is never
    // part of the record — only whether it matched.
    void recordPractice({
      lessonId: lesson.id,
      task: 'guided-ink',
      outcome: passed ? 'passed' : 'missed',
      startedAt: startedAtRef.current,
    });
    startedAtRef.current = Date.now();
  };

  const answerEar = (choice: string) => {
    if (!example) return;
    const correct = choice === example.answer;
    setEarVerdict(correct ? `Yes — ${example.answer}.` : `That was ${example.answer}.`);
    update(lesson, (p) => recordEar(p, correct, Date.now()));

    // One session per *run*, not per question. A run ends when an answer is
    // wrong or when it reaches the lesson's target — both are the completion of
    // an ear-training exercise. Recording every question instead would be a
    // keystroke log with a nicer name.
    const runSoFar = correct ? earRunRef.current + 1 : earRunRef.current;
    const reachedTarget = correct && runSoFar >= lesson.masteryRule.earStreak;
    if (!correct || reachedTarget) {
      void recordPractice({
        lessonId: lesson.id,
        task: 'ear-training',
        outcome: reachedTarget ? 'passed' : 'missed',
        correctInRun: runSoFar,
        startedAt: startedAtRef.current,
      });
      earRunRef.current = 0;
      startedAtRef.current = Date.now();
    } else {
      earRunRef.current = runSoFar;
    }

    // Next question, so the run continues rather than replaying the same example.
    setTimeout(() => setExample(buildEarExample(lesson.earTask)), 0);
  };

  if (!loaded) {
    return (
      <div className="feature">
        <p className="empty">Loading your progress…</p>
      </div>
    );
  }

  return (
    <div className="feature learn">
      <header className="feature-head">
        <div>
          <p className="eyebrow">Learn</p>
          <h2>Cause first, then the name.</h2>
          <p className="lede">
            Pick a unit. Write it, hear it, then use it.
          </p>
        </div>
      </header>

      {due.length ? (
        <p className="review-banner">
          {due.length} unit{due.length === 1 ? '' : 's'} due for review:{' '}
          {due.map((l) => (
            <button key={l.id} type="button" className="mm-note-chip" onClick={() => setSelectedId(l.id)}>
              {l.title}
            </button>
          ))}
        </p>
      ) : null}

      <div className="learn-layout">
        <nav className="lesson-path" aria-label="Learning path">
          <ol>
            {LESSONS.map((l) => {
              const p = progress[l.id] ?? emptyProgress(l.id);
              const done = isMastered(l, p);
              const open = isUnlocked(l, progress);
              return (
                <li key={l.id}>
                  <button
                    type="button"
                    className={`lesson-link ${l.id === lesson.id ? 'is-active' : ''} ${
                      done ? 'is-done' : ''
                    } ${open ? '' : 'is-locked'}`}
                    aria-current={l.id === lesson.id ? 'true' : undefined}
                    onClick={() => setSelectedId(l.id)}
                  >
                    <span className="lesson-number">{done ? '\u2713' : l.sequence}</span>
                    <span className="lesson-title">{l.title}</span>
                    {!open ? <span className="mm-visually-hidden">Locked</span> : null}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <section className="lesson-body">
          <p className="eyebrow">Unit {lesson.sequence}</p>
          <h3 className="lesson-heading">{lesson.title}</h3>
          <p className="lesson-idea">{lesson.idea}</p>

          {!unlocked ? (
            <p className="caveat">
              Locked until you have mastered{' '}
              {lesson.prerequisites
                .map((id) => LESSONS.find((l) => l.id === id)?.title ?? id)
                .join(' and ')}
              . You can read it, but the tasks are more useful in order.
            </p>
          ) : null}

          <details className="lesson-theory">
            <summary>Why it works</summary>
            <p className="cause">{lesson.cause}</p>
            {lesson.scopeNote ? (
              <p className="lesson__scope" role="note">
                {lesson.scopeNote}
              </p>
            ) : null}
          </details>

          <div className="demonstration">
            <p className="demo-caption">{lesson.demonstration.caption}</p>
            <Score
              clef={lesson.demonstration.clef}
              groups={demoGroups}
              labels={lesson.demonstration.labels}
              height={190}
            />
            <div className="play-row">
              <Button
                variant="primary"
                icon="play"
                onClick={() => playPitches(demoGroups.flat(), 'arpeggio')}
              >
                Hear the example
              </Button>
              <Button variant="tertiary" icon="stop" onClick={stop}>
                Stop
              </Button>
            </div>
          </div>

          <div className="mastery">
            <p className="mastery-rule"><strong>{mastered ? 'Mastered' : 'Your progress'}</strong></p>
            <ul className="mastery-state">
              <li className={current.inkPassed ? 'is-met' : ''}>
                Write {current.inkPassed ? '✓' : '·'}
              </li>
              <li className={current.earBest >= lesson.masteryRule.earStreak ? 'is-met' : ''}>
                Hear {current.earBest}/{lesson.masteryRule.earStreak}
              </li>
              <li className={current.creativeDone ? 'is-met' : ''}>
                Use {current.creativeDone ? '✓' : lesson.masteryRule.requireCreative ? '·' : '—'}
              </li>
            </ul>
            <details className="mastery-goal">
              <summary>Goal</summary>
              <p>{lesson.masteryRule.description}</p>
            </details>
          </div>

          <Tabs
            label="Tasks"
            idPrefix="learn-task"
            value={tab}
            onChange={setTab}
            options={[
              { value: 'write', label: 'Write it', hint: current.inkPassed ? 'Done' : 'On the staff' },
              { value: 'hear', label: 'Hear it', hint: `${current.earBest}/${lesson.masteryRule.earStreak} right` },
              { value: 'use', label: 'Use it', hint: current.creativeDone ? 'Done' : 'Make your own' },
            ]}
          />

          <TabPanel idPrefix="learn-task" value="write" active={tab === 'write'}>
            <div className="task">
              <p className="task-prompt">{lesson.guidedInkTask.prompt}</p>
              <WritingPad
                key={`ink-${lesson.id}`}
                ref={inkPadRef}
                clef={lesson.guidedInkTask.clef}
                onResult={setInkResult}
                height={320}
                hint={lesson.guidedInkTask.hint}
              />
              <div className="practice-actions">
                <Button variant="primary" onClick={checkInk}>
                  Check
                </Button>
                <Button
                  variant="tertiary"
                  onClick={() => {
                    inkPadRef.current?.clear();
                    setInkVerdict(null);
                  }}
                >
                  Clear
                </Button>
              </div>
              {inkVerdict ? (
                <p role="status" className={`grade ${inkVerdict === 'Correct.' ? 'is-pass' : 'is-fail'}`}>
                  {inkVerdict}
                </p>
              ) : null}
            </div>
          </TabPanel>

          <TabPanel idPrefix="learn-task" value="hear" active={tab === 'hear'}>
            <div className="task">
              <p className="task-prompt">{lesson.earTask.prompt}</p>
              <div className="play-row">
                <Button
                  variant="primary"
                  icon="play"
                  onClick={() => example && playPitches(example.pitches, example.mode)}
                >
                  Play it
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setExample(buildEarExample(lesson.earTask))}
                >
                  New example
                </Button>
                <span className="streak">Run of {current.earStreak}</span>
              </div>
              <div className="ear-choices" role="group" aria-label="Answer choices">
                {lesson.earTask.choices.map((choice) => (
                  <Button key={choice} variant="secondary" onClick={() => answerEar(choice)}>
                    {choice}
                  </Button>
                ))}
              </div>
              {earVerdict ? (
                <p role="status" className={`grade ${earVerdict.startsWith('Yes') ? 'is-pass' : 'is-fail'}`}>
                  {earVerdict}
                </p>
              ) : null}
            </div>
          </TabPanel>

          <TabPanel idPrefix="learn-task" value="use" active={tab === 'use'}>
            <div className="task">
              <p className="task-prompt">{lesson.creativeTask.prompt}</p>
              <WritingPad
                key={`creative-${lesson.id}`}
                clef={lesson.creativeTask.clef}
                height={320}
                hint="Nothing here is graded. Write, then listen to what you wrote."
              />
              <div className="practice-actions">
                <Button
                  variant="primary"
                  onClick={() => {
                    update(lesson, (p) => recordCreative(p, Date.now()));
                    void recordPractice({
                      lessonId: lesson.id,
                      task: 'creative',
                      outcome: 'completed',
                      startedAt: startedAtRef.current,
                    });
                    startedAtRef.current = Date.now();
                    setCreativeShown(true);
                  }}
                >
                  Mark done
                </Button>
              </div>
              {creativeShown || current.creativeDone ? (
                <p className="cause" role="status">
                  <strong>What this was for:</strong> {lesson.creativeTask.lookFor}
                </p>
              ) : null}
            </div>
          </TabPanel>
        </section>
      </div>
    </div>
  );
}
